import React, { useState, useEffect } from "react";
import { X, MapPin, Building2, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import { AddressSelectionModalProps, PatientAddress, NearestClinicData } from "./types";
import { AddressService } from "./address.service";
import AddressList from "./AddressList";
import AddAddressWizard from "./AddAddressWizard";
import NearestClinicCard from "./NearestClinicCard";

export const AddressSelectionModal: React.FC<AddressSelectionModalProps> = ({
  isOpen,
  onClose,
  patientId,
  orderId,
  currentAddress,
  currentPincode,
  currentClinicId,
  onAddressUpdated,
}) => {
  const [viewMode, setViewMode] = useState<"list" | "add">("list");

  // Patient Addresses State
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addresses, setAddresses] = useState<PatientAddress[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<PatientAddress | null>(null);

  // Nearest Clinic State
  const [loadingClinic, setLoadingClinic] = useState(false);
  const [nearestClinic, setNearestClinic] = useState<NearestClinicData | null>(null);
  const [clinicError, setClinicError] = useState<string | null>(null);

  // Submit State
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isOpen && patientId) {
      fetchAddresses();
    } else {
      resetState();
    }
  }, [isOpen, patientId]);

  const resetState = () => {
    setViewMode("list");
    setAddresses([]);
    setSelectedAddress(null);
    setNearestClinic(null);
    setClinicError(null);
  };

  const fetchAddresses = async () => {
    try {
      setLoadingAddresses(true);
      const res = await AddressService.fetchPatientAddresses(patientId);
      if (res.success && Array.isArray(res.data)) {
        setAddresses(res.data);
        // Pre-select matching or default address if available
        if (res.data.length > 0) {
          const matched = res.data.find(
            (a) => a.address?.toLowerCase() === currentAddress?.toLowerCase()
          );
          const defaultAddr = res.data.find((a) => a.isDefault) || res.data[0];
          const target = matched || defaultAddr;
          handleSelectAddress(target);
        }
      } else {
        setAddresses([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load patient addresses");
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleSelectAddress = async (address: PatientAddress) => {
    setSelectedAddress(address);
    setClinicError(null);
    fetchNearestClinicForAddress(address);
  };

  const fetchNearestClinicForAddress = async (address: PatientAddress) => {
    try {
      setLoadingClinic(true);
      const lat = address.latitude || 26.8467;
      const lng = address.longitude || 80.9462;
      const fullAddr = `${address.address}, ${address.city || ""}, ${address.state || ""}`.trim();

      const res = await AddressService.getNearestClinic({
        patientAddress: fullAddr,
        pincode: address.pincode,
        latitude: lat,
        longitude: lng,
      });

      if (res.success && res.data) {
        setNearestClinic(res.data);
        setClinicError(null);
      } else {
        setNearestClinic(null);
        setClinicError(res.message || "Unable to calculate nearest clinic for this location.");
      }
    } catch (err) {
      console.error(err);
      setNearestClinic(null);
      setClinicError("Failed to connect to nearest clinic service.");
    } finally {
      setLoadingClinic(false);
    }
  };

  const handleAddressCreated = (newAddress: PatientAddress) => {
    setAddresses((prev) => [newAddress, ...prev]);
    setViewMode("list");
    handleSelectAddress(newAddress);
  };

  const handleUpdateOrderAddress = async () => {
    if (!selectedAddress) {
      toast.error("Please select a delivery address");
      return;
    }
    if (!nearestClinic && !currentClinicId) {
      toast.error("Nearest clinic information is required before updating address.");
      return;
    }

    const assignedClinicId = nearestClinic?.clinicId || currentClinicId || "";

    const payload = {
      orderId,
      addressId: selectedAddress.id,
      address: `${selectedAddress.address}, ${selectedAddress.city}, ${selectedAddress.state}`,
      pincode: selectedAddress.pincode,
      clinicId: assignedClinicId,
    };

    try {
      setUpdating(true);
      const res = await AddressService.updateOrderAddress(payload);
      if (res.success) {
        toast.success(res.message || "Order address updated successfully!");
        if (onAddressUpdated) {
          onAddressUpdated({
            address: payload.address,
            pincode: payload.pincode,
            clinicId: payload.clinicId,
            clinicName: nearestClinic?.clinicName,
          });
        }
        onClose();
      } else {
        toast.error(res.message || "Failed to update order address");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update order address");
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[500000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-[2px] animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600">
              <MapPin size={19} />
            </div>
            <div>
              <h3 className="text-sm font-extrabold text-slate-800">
                Edit Delivery Address & Assigned Clinic
              </h3>
              <p className="text-[0.7rem] text-slate-500 font-medium">
                Order #{orderId} — Select or add a new address
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={updating}
            className="w-8 h-8 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-colors cursor-pointer disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 flex flex-col gap-4">
          {viewMode === "list" ? (
            <>
              <AddressList
                loading={loadingAddresses}
                addresses={addresses}
                selectedAddress={selectedAddress}
                onSelectAddress={handleSelectAddress}
                onAddNew={() => setViewMode("add")}
              />

              {/* Nearest Clinic Card */}
              {selectedAddress && (
                <div className="pt-2 border-t border-slate-100">
                  <NearestClinicCard
                    loading={loadingClinic}
                    clinic={nearestClinic}
                    error={clinicError}
                    onRetry={() => fetchNearestClinicForAddress(selectedAddress)}
                  />
                </div>
              )}
            </>
          ) : (
            <AddAddressWizard
              patientId={patientId}
              onAddressCreated={handleAddressCreated}
              onCancel={() => setViewMode("list")}
            />
          )}
        </div>

        {/* Footer */}
        {viewMode === "list" && (
          <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <span className="text-[0.68rem] text-slate-500 font-medium">
              {selectedAddress
                ? `Selected: ${selectedAddress.addressName || "Address"} (${selectedAddress.pincode})`
                : "Please select an address"}
            </span>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={updating}
                className="px-4 py-2 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 rounded-xl transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateOrderAddress}
                disabled={updating || !selectedAddress || loadingClinic}
                className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm"
              >
                {updating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Updating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={14} /> Update Order Address
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressSelectionModal;
